// ----- Phase 5.X-submission-reorder — Reorder API client ----- //
//
// Two endpoints:
//   - apply():            POST /api/v1/submissions/reorder
//   - evaluateLifecycle(): POST /api/v1/submissions/reorder/evaluate-lifecycle
//
// Kept in `web/src/lib/api/` (alongside `import.ts` and `compiler.ts`)
// rather than folded into `v2Client.ts` to keep that file focused on
// the core CRUD endpoints. The reorder surface is a single feature
// (HomePage's ↕ icon → ReorderModal) so it doesn't need to live next
// to every other submission-shaped endpoint.

import { apiFetch } from '@/api/v2Client';

// ----- Request / response shapes ----------------------------------------------

export interface ReorderEntry {
  /** Submission ID (UUID). */
  readonly id: string;
  /** Existing sequence-number slot assigned to this row; original 0000 stays fixed. */
  readonly newSequenceNumber: number;
}

export interface ReorderRequest {
  readonly nda: string;
  readonly sequenceOrder: readonly ReorderEntry[];
}

export interface ReorderApplyResult {
  /** ISO timestamp the server recorded for this reorder event. */
  readonly reorderedAt: string;
  /** Resulting order, sorted by the new sequence number (asc). */
  readonly newOrder: ReadonlyArray<{ id: string; sequenceNumber: string }>;
}

// Evaluate-lifecycle input uses the new sequence number AS the sequence number
// to evaluate against (not "newSequenceNumber") — it's a hypothetical.
export interface EvaluateLifecycleEntry {
  readonly id: string;
  readonly sequenceNumber: number;
}

export interface EvaluateLifecycleRequest {
  readonly nda: string;
  readonly sequenceOrder: readonly EvaluateLifecycleEntry[];
}

export interface AffectedDocument {
  readonly documentId: string;
  readonly documentName: string;
  readonly sequenceId: string;
  readonly currentLifecycleOperation: string;
  readonly currentLifecycleTarget: string;
  /**
   * V2's lifecycle target is a document ID, not a sequence number, so a
   * deterministic auto-fix isn't possible — operator must pick a new
   * ReplacesDocumentId in the Document Inspector. Always `null` in V2;
   * kept in the shape for future use and parity with V1's report.
   */
  readonly suggestedNewTarget: number | null;
  readonly autoFixable: boolean;
  readonly impactDescription: string;
}

export interface EvaluateLifecycleResult {
  readonly affectedDocuments: readonly AffectedDocument[];
  readonly unaffectedDocumentCount: number;
}

// ----- Client -----------------------------------------------------------------

export const reorderApi = {
  /** Apply a reorder atomically. 400 on locked-sequence or NDA mismatch. */
  apply: async (req: ReorderRequest): Promise<ReorderApplyResult> => {
    return apiFetch<ReorderApplyResult>('/api/v1/submissions/reorder', {
      method: 'POST',
      json: req,
    });
  },

  /** Read-only: compute the lifecycle impact of a proposed reorder. */
  evaluateLifecycle: async (
    req: EvaluateLifecycleRequest,
  ): Promise<EvaluateLifecycleResult> => {
    return apiFetch<EvaluateLifecycleResult>(
      '/api/v1/submissions/reorder/evaluate-lifecycle',
      { method: 'POST', json: req },
    );
  },
};
